import { useCallback, useMemo, useState } from 'react';
import { useGetBookingByIdQuery } from '../../Bookings/Service/bookingsApi';
import { useGetBookingSearchV2ListQuery } from '../../Bookings/Service/extendedBookingsApi';
import { useDebounce } from '../../Shared/Hooks/useDebounce';
import { mapBookingToReservationLookupOption } from '../Helpers/mapBookingToReservationLookupOption';
import {
  ReservationBookingLookupOption,
  ReservationSelectedBookingContext,
} from '../Types/reservationBookingContext.types';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_LOOKUP_LENGTH = 3;
const DEFAULT_LOOKUP_PAGE_SIZE = 20;

type ReservationBookingLookupInputReason = 'input' | 'clear' | 'open';

interface UseReservationBookingContextParams {
  bookingId?: number | null;
  debounceMs?: number;
  minLookupLength?: number;
  lookupPageSize?: number;
}

interface UseReservationBookingContextResult {
  selectedBookingContext?: ReservationSelectedBookingContext;
  bookingLookupOptions: ReservationBookingLookupOption[];
  selectedBookingLoading: boolean;
  bookingLookupLoading: boolean;
  handleBookingLookupInputChange: (
    inputValue: string,
    reason?: ReservationBookingLookupInputReason
  ) => void;
}

const toValidBookingId = (value: number | null | undefined): number | null => {
  if (!Number.isInteger(value) || Number(value) <= 0) {
    return null;
  }

  return Number(value);
};

export const useReservationBookingContext = ({
  bookingId,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minLookupLength = DEFAULT_MIN_LOOKUP_LENGTH,
  lookupPageSize = DEFAULT_LOOKUP_PAGE_SIZE,
}: UseReservationBookingContextParams): UseReservationBookingContextResult => {
  const [bookingLookupSearch, setBookingLookupSearch] = useState('');
  const debouncedLookupSearch = useDebounce(bookingLookupSearch, debounceMs);
  const selectedBookingId = toValidBookingId(bookingId);
  const normalizedSearch = debouncedLookupSearch.trim();
  const shouldRunLookupSearch = normalizedSearch.length >= minLookupLength;

  const { data: selectedBooking, isLoading: selectedBookingLoading, isFetching: selectedBookingFetching } =
    useGetBookingByIdQuery(selectedBookingId ?? 0, { skip: !selectedBookingId });

  const { data: bookingLookupData, isLoading: bookingLookupLoading, isFetching: bookingLookupFetching } =
    useGetBookingSearchV2ListQuery(
      {
        searchTerm: shouldRunLookupSearch ? normalizedSearch : undefined,
        dateFilterMode: 'startDate',
        pageNumber: 1,
        pageSize: lookupPageSize,
      },
      {
        skip: !shouldRunLookupSearch,
        refetchOnMountOrArgChange: true,
      }
    );

  const selectedBookingContext = useMemo<ReservationSelectedBookingContext | undefined>(() => {
    if (!selectedBooking || !selectedBookingId) {
      return undefined;
    }

    return {
      id: selectedBooking.id,
      offerId: selectedBooking.offerId,
      offerName: selectedBooking.offerName,
      bookingDateFrom: selectedBooking.bookingDateFrom,
      bookingDateTo: selectedBooking.bookingDateTo,
      price: selectedBooking.price,
    };
  }, [selectedBooking, selectedBookingId]);

  const selectedBookingOption = useMemo(
    () => (selectedBookingContext ? mapBookingToReservationLookupOption(selectedBookingContext) : undefined),
    [selectedBookingContext]
  );

  const lookupOptions = useMemo(
    () => (bookingLookupData?.items ?? []).map((booking) => mapBookingToReservationLookupOption(booking)),
    [bookingLookupData?.items]
  );

  const bookingLookupOptions = useMemo(() => {
    if (!selectedBookingOption) {
      return lookupOptions;
    }

    const includesSelected = lookupOptions.some((option) => option.value === selectedBookingOption.value);
    return includesSelected ? lookupOptions : [selectedBookingOption, ...lookupOptions];
  }, [lookupOptions, selectedBookingOption]);

  const handleBookingLookupInputChange = useCallback(
    (inputValue: string, reason?: ReservationBookingLookupInputReason) => {
      if (reason === 'open') {
        return;
      }

      if (reason === 'clear') {
        setBookingLookupSearch('');
        return;
      }

      setBookingLookupSearch(inputValue);
    },
    []
  );

  return {
    selectedBookingContext,
    bookingLookupOptions,
    selectedBookingLoading: selectedBookingLoading || selectedBookingFetching,
    bookingLookupLoading:
      shouldRunLookupSearch && (bookingLookupLoading || bookingLookupFetching),
    handleBookingLookupInputChange,
  };
};
